import {employeeHistoryEventLabel, employeeHistoryTimestamp, employeeSourceLabel} from './employeeHistoryPresentation';
import {workforceHistoryDescription, historyPeriod} from '@/utils/employeeWorkforceHistory';

const IMPORT_EVENTS = new Set([
    'one_c_import_created', 'one_c_import_updated', 'google_workforce_imported', 'imported',
    'argus_user_synchronized', 'timesheet_presence_baseline', 'arrival_baseline_accepted',
    'google_contacts_imported', 'skills_imported', 'workforce_plan_final_import_applied'
]);
const SOURCE_LABELS = {
    timesheet: 'Табель', timesheet_baseline: 'Перенос данных табелей',
    manual_termination: 'Кадровое изменение', scheduled_termination: 'По запланированной дате',
    workforce_plan: 'План сотрудников', migration: 'Перенос данных',
    google_workforce_2026: 'Google-таблица «Движение 2026»'
};
const CARD_EVENT_LABELS = {
    google_contacts_imported: 'Карточка дополнена из Google Контактов',
    skills_imported: 'Перенесены навыки сотрудника',
    workforce_plan_final_import_applied: 'План перенесён из таблицы «Движение 2026»',
    employment_period_reconciled: 'Период работы подтверждён данными 1С',
    object_assignment_ended_due_lifecycle: 'Дополнительное назначение завершено'
};

export function cardHistorySource(source) {
    const label = SOURCE_LABELS[source] || employeeSourceLabel(source);
    return label === source ? 'Другой источник' : label;
}

export function cardHistoryCategory(event) {
    const type = String(event.event_type || '');
    if (event.source === 'timesheet_roster') return {label: 'Табель', icon: 'fa-calendar-alt', tone: 'blue'};
    if (IMPORT_EVENTS.has(type)) return {label: 'Перенос и сверка', icon: 'fa-database', tone: 'muted', imported: true};
    if (/characteristic/.test(type)) return {label: 'Характеристика', icon: 'fa-comment-alt', tone: 'mint'};
    if (/assignment|planned_move|workforce|arrival/.test(type)) return {label: 'Назначения', icon: 'fa-map-marker-alt', tone: 'blue'};
    if (/termination|lifecycle|employment|hiring|reserve_returned/.test(type)) return {label: 'Кадровые события', icon: 'fa-briefcase', tone: 'sand'};
    if (/absence/.test(type)) return {label: 'Отсутствие', icon: 'fa-calendar-minus', tone: 'sand'};
    return {label: 'Карточка', icon: 'fa-user-edit', tone: 'muted'};
}

export function presentCardHistory(event, rows, description) {
    const category = cardHistoryCategory(event);
    const type = event.event_type;
    let title = CARD_EVENT_LABELS[type] || employeeHistoryEventLabel(event);
    const baseline = ['timesheet_presence_baseline', 'arrival_baseline_accepted'].includes(type);
    if (baseline) {
        title = 'Учтены ранее заполненные табели';
        description = 'При переносе данных учтено присутствие по сохранённым табелям. Дата прибытия не менялась.';
        rows = [];
    } else if (category.imported) {
        // Import descriptions may contain database names and batch keys. The title and
        // explicitly formatted field changes convey the result without those internals.
        description = '';
    } else if (event.context && event.context.plan) {
        description = workforceHistoryDescription(event);
        rows = [];
    } else if (type === 'workforce_plan_range_changed') {
        const changes = event.changes || {};
        description = `Период: ${historyPeriod(changes.starts_on, changes.ends_on)}.`;
        rows = rows.filter(row => row.key !== 'period');
    }
    if (!description || !/[а-яёa-z0-9]/i.test(description)
        || description.replace(/[.\s]+$/, '') === title) description = '';
    if (description.startsWith(`${title}.`)) description = description.slice(title.length + 1).trim();
    // Dates in generated descriptions are date-only values, not timestamps.
    description = description.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, '$3.$2.$1');
    let comment = type === 'reserve_returned' ? (event.changes || {}).reason : null;
    if (/^employee_termination_/.test(type) || type === 'employment_period_ended' && (event.changes || {}).termination_reason) {
        comment = event.description || (event.changes || {}).termination_reason || null;
        description = '';
        rows = rows.filter(row => !['reason', 'termination_reason'].includes(row.key));
    }
    if (type === 'reserve_returned') { title = 'Возврат из резерва'; rows = rows.filter(row => row.key !== 'reason'); }
    const primaryKeys = ['reason', 'lifecycle_status', 'content', 'ktu', 'arrival_date', 'effective_date'];
    if (type === 'workforce_plan_range_changed') primaryKeys.push('state_type', 'building_id', 'object_group_id', 'cleared', 'loupe_reason');
    const primary = event.source === 'timesheet_roster' && description ? []
        : rows.filter(row => primaryKeys.includes(row.key));
    if (!description && !primary.length && !category.imported) primary.push(...rows.slice(0, 2));
    const changes = event.changes || {};
    return {
        id: event.id, title, description, category, comment,
        timestamp: employeeHistoryTimestamp(event),
        actorId: event.actor_user_id, actorName: event.actor_name,
        source: cardHistorySource(event.source),
        primary, details: rows.filter(row => !primary.includes(row)),
        requestId: changes.request_id, requestNumber: changes.request_number,
        financialAuditIds: Array.isArray(changes.financial_audit_ids) ? changes.financial_audit_ids : []
    };
}
